import React, { useState } from 'react';
import { openCertificate } from '@/lib/certificates';
import { Award, Star, Lock, Check, ChevronRight, Download, Eye, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return ''; }
};

const tint = (hex, alpha) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

const Metric = ({ label, children, testId }) => (
  <div className="bg-white/70 rounded-2xl px-5 py-4" data-testid={testId}>
    <p className="text-[28px] leading-none font-bold text-[#1F2937] font-['Outfit'] tracking-tight flex items-center gap-1.5">
      {children}
    </p>
    <p className="text-sm text-[#6B7280] mt-2">{label}</p>
  </div>
);

export function ImpactAndRecognition({ impact }) {
  const [open, setOpen] = useState(false);
  if (!impact) return null;

  const completed = impact.completed_experiences || 0;
  const next = impact.next_milestone;
  const pct = Math.max(Math.min(impact.progress_percent ?? 0, 100), 0);
  const nearly = next && next.remaining <= 3;
  const accent = next ? next.color : '#5B4AA1';

  return (
    <>
      <div className="rounded-2xl border border-[#FBDED7] bg-[#FFF7F5] p-6 sm:p-7" data-testid="impact-recognition-panel">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-[#1F2937] font-['Outfit'] tracking-tight">Your Impact &amp; Recognition</h2>
            {impact.highest_awarded ? (
              <p className="text-sm mt-1 flex items-center gap-1.5" data-testid="highest-awarded">
                <Award className="h-3.5 w-3.5" style={{ color: impact.highest_awarded.color }} />
                <span className="font-semibold" style={{ color: impact.highest_awarded.color }}>{impact.highest_awarded.title}</span>
                <span className="text-[#9CA3AF]">· recognised by your school</span>
              </p>
            ) : (
              <p className="text-sm text-[#6B7280] mt-1">Every experience inspires a future.</p>
            )}
          </div>
          <Award className="h-5 w-5 shrink-0" style={{ color: accent }} />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
          <Metric label="Mentoring Experiences Completed" testId="metric-completed">{completed}</Metric>
          <Metric label="Student Participations" testId="metric-participations">{impact.student_participations || 0}</Metric>
          <Metric label="Students Reached" testId="metric-students">{impact.students_reached || 0}</Metric>
          <Metric
            label={impact.rating_count ? `Overall Rating · ${impact.rating_count} review${impact.rating_count === 1 ? '' : 's'}` : 'Overall Rating'}
            testId="metric-rating"
          >
            {impact.rating
              ? <>{impact.rating}<Star className="h-5 w-5 text-[#F5A623] fill-[#F5A623]" /></>
              : <span className="text-[#9CA3AF]">—</span>}
          </Metric>
        </div>

        <div className="mt-6 pt-6 border-t border-[#FBDED7]">
          {next ? (
            <>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-wider">Next Recognition</p>
                  <p className="flex items-center gap-2 mt-2">
                    <Award className="h-4 w-4" style={{ color: next.color }} />
                    <span className="text-base font-bold text-[#1F2937] font-['Outfit']" data-testid="next-recognition-title">{next.title}</span>
                    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ color: next.color, backgroundColor: tint(next.color, 0.12) }}>
                      {next.threshold} experiences
                    </span>
                  </p>
                  <p className="text-sm text-[#6B7280] mt-1.5" data-testid="next-recognition-progress">
                    {completed} of {next.threshold} experiences completed
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold font-['Outfit'] tracking-tight" style={{ color: next.color }} data-testid="next-recognition-percent">{pct}%</p>
                  <p className="text-sm text-[#6B7280] mt-0.5" data-testid="next-recognition-remaining">{next.remaining} more to go</p>
                </div>
              </div>
              <div className="h-2 rounded-full bg-[#F1F2F4] mt-4 overflow-hidden">
                <div
                  className="h-full rounded-full transition-[width] duration-700"
                  style={{ width: `${pct}%`, backgroundColor: next.color }}
                  data-testid="next-recognition-bar"
                />
              </div>
              {nearly && (
                <p className="text-sm text-[#6B7280] mt-3" data-testid="recognition-encouragement">
                  Just {next.remaining} more experience{next.remaining === 1 ? '' : 's'} to reach{' '}
                  <span className="font-semibold" style={{ color: next.color }}>{next.title}</span>.
                </p>
              )}
            </>
          ) : (
            <p className="text-sm font-semibold" style={{ color: accent }} data-testid="next-recognition-title">
              Every recognition level reached — thank you for {completed} experiences.
            </p>
          )}

          <div className="flex justify-end mt-4">
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-1 text-sm font-medium text-[#6B7280] hover:text-[#1F2937] transition-colors"
              data-testid="view-all-milestones-btn"
            >
              View all milestones <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <MilestonesModal open={open} onOpenChange={setOpen} impact={impact} />
    </>
  );
}

function MilestonesModal({ open, onOpenChange, impact }) {
  const milestones = impact.milestones || [];
  const principalApproved = !!impact.principal_approved;
  const completed = impact.completed_experiences || 0;
  const nextThreshold = impact.next_milestone?.threshold;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-2xl" data-testid="milestones-modal">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-lg">Recognition Milestones</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#6B7280] -mt-2">
          {completed} verified experience{completed === 1 ? '' : 's'} completed at your school.
        </p>

        <div className="space-y-3 mt-2 max-h-[60vh] overflow-y-auto pr-1">
          {milestones.map(m => (
            <div
              key={m.threshold}
              className="rounded-2xl border p-4"
              style={{
                borderColor: m.achieved ? tint(m.color, 0.35) : '#EFF0F2',
                backgroundColor: m.achieved ? tint(m.color, 0.05) : '#FFFFFF',
              }}
              data-testid={`milestone-row-${m.threshold}`}
            >
              <div className="flex items-start gap-3">
                <div
                  className="h-11 w-11 rounded-xl flex flex-col items-center justify-center shrink-0"
                  style={{ backgroundColor: tint(m.color, m.achieved ? 0.16 : 0.08) }}
                >
                  <Award className="h-4 w-4" style={{ color: m.color }} />
                  <span className="text-[10px] font-bold" style={{ color: m.color }}>{m.threshold}</span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-bold text-[#1F2937] font-['Outfit']">{m.title}</p>
                    <StatusChip m={m} isNext={m.threshold === nextThreshold} />
                  </div>
                  <p className="text-sm text-[#6B7280] mt-0.5">
                    {m.threshold} completed experiences
                  </p>
                  <p className="text-xs text-[#9CA3AF] mt-1" data-testid={`milestone-detail-${m.threshold}`}>
                    {m.awarded
                      ? `Recognised by your school${m.awarded_at ? ` on ${fmtDate(m.awarded_at)}` : ''}`
                      : m.achieved
                        ? `Reached${m.achieved_at ? ` on ${fmtDate(m.achieved_at)}` : ''}${m.certificate_url ? ' · certificate ready, awaiting school recognition' : ' · awaiting school recognition'}`
                        : `${m.remaining} more experience${m.remaining === 1 ? '' : 's'} to achieve`}                  </p>
                  {m.certificate_id && (
                    <p className="text-[10px] text-[#C3C7CE] mt-1 font-mono" data-testid={`milestone-cert-id-${m.threshold}`}>{m.certificate_id}</p>
                  )}
                  {m.achieved && (
                    <CertificateAction milestone={m} principalApproved={principalApproved} />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CertificateAction({ milestone, principalApproved }) {
  const [loading, setLoading] = useState(false);

  if (milestone.awarded && milestone.certificate_url) {
    return (
      <Button variant="outline" onClick={() => openCertificate(milestone.certificate_url)} className="mt-3 h-8 rounded-lg text-xs border-[#E5E7EB]" data-testid={`milestone-certificate-${milestone.threshold}`}>
        <Download className="h-3.5 w-3.5 mr-1.5" /> Download certificate
      </Button>
    );
  }

  const openPreview = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/mentor/recognitions/${milestone.threshold}/preview`);
      await openCertificate(r.data.url);
    } catch {
      toast.error('Could not open the preview');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-3">
      <Button variant="outline" className="h-8 rounded-lg text-xs border-[#E5E7EB]" onClick={openPreview} disabled={loading} data-testid={`milestone-preview-${milestone.threshold}`}>
        {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Eye className="h-3.5 w-3.5 mr-1.5" /> Preview certificate</>}
      </Button>
      <p className="text-[10px] text-[#C3C7CE] mt-1.5" data-testid={`milestone-preview-note-${milestone.threshold}`}>
        {principalApproved
          ? 'Your school has not sent this recognition yet.'
          : "Preview only — your school has not yet approved the principal's name for certificates."}
      </p>
    </div>
  );
}

function StatusChip({ m, isNext }) {
  const base = 'text-[10px] font-semibold px-2 py-0.5 rounded-full shrink-0 inline-flex items-center gap-1';
  if (m.awarded) {
    return <span className={base} style={{ color: m.color, backgroundColor: tint(m.color, 0.14) }} data-testid={`milestone-status-${m.threshold}`}><Check className="h-3 w-3" /> Awarded</span>;
  }
  if (m.achieved) {
    return <span className={base} style={{ color: m.color, backgroundColor: tint(m.color, 0.1) }} data-testid={`milestone-status-${m.threshold}`}><Check className="h-3 w-3" /> Achieved</span>;
  }
  if (isNext) {
    return <span className={`${base} text-[#F97D6A] bg-[#FFF0ED]`} data-testid={`milestone-status-${m.threshold}`}>Next</span>;
  }
  return <span className={`${base} text-[#9CA3AF] bg-[#F3F4F6]`} data-testid={`milestone-status-${m.threshold}`}><Lock className="h-3 w-3" /> Upcoming</span>;
}
